import axios from "axios";

// Business documents (snb_lightspeed_backend docs/customer-module/kyc/03-File-Storage.md
// §11, 16-USSD-Registration-Business-Documents-and-Fees.md §3): the business
// visits of a customer and their photos and PDFs, read through the member, the
// application or a loan application. No approval: `view business documents`
// reads, `manage business documents` writes. Downloads go through
// kycfiles/downloadUrl like every other file.
//
// Every action resolves to the whole envelope (res.data) and rejects with what
// the global interceptor rejects with: the axios response. Errors arrive under
// `data` (customer journey routes).

// the route prefix of each context
const BASES = Object.freeze({
    customer: "customers",
    application: "customer-applications",
    loan: "loan-applications",
});

function historyUrl(context, id) {
    const base = BASES[context];
    if (!base) throw new Error(`Unknown business documents context: ${context}`);
    return `/api/v1/${base}/${id}/business-visits`;
}

export default {
    namespaced: true,

    state: {},

    getters: {},

    mutations: {},

    actions: {
        // GET /{customers|customer-applications|loan-applications}/{id}/business-visits
        // data: {loan_application_id, last_visit_date (Y-m-d|null),
        //   visits: [{business_visit_id, visit_date, visited_by, visited_by_name, note,
        //             loan_application_id, file_count, files: [file block],
        //             removed_files: [file block + removed_at, removed_by_name], created_at}],
        //   document_types: [{document_type_id, code, name, file_rules}]}
        // Newest visit first; on a loan, the visits used for it first.
        async fetchVisits({ commit }, { context, id }) {
            return await axios
                .get(historyUrl(context, id))
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // POST the same route: {visit_date (Y-m-d), note?, loan_application_id?};
        // on a loan application the visit is used for that loan. data: the visit.
        async createVisit({ commit }, { context, id, payload }) {
            return await axios
                .post(historyUrl(context, id), payload, { showLoader: false })
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // POST /business-visits/{id}/files (multipart: file, document_type_id;
        // optional Idempotency-Key). data: the file block, attached to the visit.
        async uploadFile({ commit }, { visitId, documentTypeId, file, idempotencyKey, onProgress }) {
            const form = new FormData();
            form.append("file", file);
            form.append("document_type_id", documentTypeId);

            const config = { showLoader: false, headers: {} };
            if (idempotencyKey) config.headers["Idempotency-Key"] = idempotencyKey;
            if (typeof onProgress === "function") {
                config.onUploadProgress = (event) => {
                    if (event && event.total) onProgress(Math.round((event.loaded * 100) / event.total));
                };
            }

            return await axios
                .post(`/api/v1/business-visits/${visitId}/files`, form, config)
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // DELETE /business-visits/{id}/files/{file_id}: hidden from the visit, kept
        async removeFile({ commit }, { visitId, fileId }) {
            return await axios
                .delete(`/api/v1/business-visits/${visitId}/files/${fileId}`, { showLoader: false })
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // DELETE /business-visits/{id}: the visit and its files leave the history, kept
        async removeVisit({ commit }, visitId) {
            return await axios
                .delete(`/api/v1/business-visits/${visitId}`, { showLoader: false })
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // PUT /loan-applications/{id}/business-visits/{visit_id}: "Use for this loan".
        // 409 data.reason tagged_elsewhere when another loan already uses it.
        async tagVisit({ commit }, { loanApplicationId, visitId }) {
            return await axios
                .put(`/api/v1/loan-applications/${loanApplicationId}/business-visits/${visitId}`, {}, { showLoader: false })
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },

        // DELETE the same route: no longer used for this loan
        async untagVisit({ commit }, { loanApplicationId, visitId }) {
            return await axios
                .delete(`/api/v1/loan-applications/${loanApplicationId}/business-visits/${visitId}`, { showLoader: false })
                .then((res) => res.data)
                .catch((err) => Promise.reject(err));
        },
    },
};
